<template>
    <div class="ppv2-w-full lg:ppv2-w-[80%]">
        <form @submit.prevent="save" class="ppv2-flex ppv2-flex-col ppv2-gap-6">
            <!-- Global Toggle Standalone -->
            <div class="ppv2-flex ppv2-items-center ppv2-mb-4">
                <BaseSwitch
                    v-model="form.enabled_menu_footer"
                    :label="$t('website_settings.footer.global_toggle')"
                    customLabelClass="poppinsLight ppv2-text-[#021832]"
                    variant="purple"
                />
            </div>

            <!-- Pied de page (Logo + Présentation) -->
            <SectionCard :title="$t('website_settings.footer.title')" :hideToggle="true">
                <div class="ppv2-grid ppv2-grid-cols-1 ppv2-gap-8">
                    <!-- Logo Upload -->
                    <div class="lg:ppv2-col-span-2">
                        <CustomInputUpload
                            :label="$t('website_settings.footer.placeholders.logo')"
                            customClassLabel="poppinsLight ppv2-mb-2 ppv2-text-gray-600"
                            v-model="form.footer_logo_url"
                            @input="(file) => handleImageUpload('footer_logo', { target: { files: [file] } })"
                            specificTaille="150 x 150 pixels recommandé"
                        />
                    </div>

                    <!-- Présentation de votre établissement -->
                    <div>
                        <BaseTextarea
                            :label="$t('website_settings.footer.presentation_section')"
                            customLabelClass="poppinsLight ppv2-text-gray-600"
                            v-model="form.footer_desc"
                            :rows="5"
                            :use-editor="false"
                            :placeholder="$t('website_settings.footer.placeholders.presentation_desc')"
                        />
                    </div>
                </div>
            </SectionCard>

            <SectionCard :title="$t('website_settings.footer.contact_section')" :hideToggle="true">
                <!-- TODO: Uncomment when InfoBanner.vue is available -->
                <InfoBanner
                    :text="$t('website_settings.footer.contact_info_banner')"
                    :linkText="$t('website_settings.footer.configure_now')"
                    :linkUrl="`/back-office/v2/establishment/${establishmentId}/edit`"
                />
            </SectionCard>

            <!-- Horaires d'ouverture -->
            <SectionCard :title="$t('website_settings.footer.opening_hours_section')" :hideToggle="true">
                <div class="ppv2-flex ppv2-flex-col ppv2-gap-6">
                    <!-- Info Banner -->
                    <!-- TODO: Uncomment when InfoBanner.vue is available -->
                    <InfoBanner>
                        {{ $t('website_settings.footer.hours_info_banner_1') }}
                        <a
                            :href="`/back-office/v2/establishment/${establishmentId}/edit`"
                            class="ppv2-text-blue-700 ppv2-underline poppinsMedium hover:ppv2-text-blue-800"
                        >
                            {{ $t('website_settings.footer.hours_info_banner_link') }} </a
                        ><br />
                        {{ $t('website_settings.footer.hours_info_banner_2') }}
                    </InfoBanner>

                    <!-- Custom Hours Toggle -->
                    <div
                        v-if="!form.enabled_footer_hours"
                        class="ppv2-relative"
                        :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20) }"
                    >
                        <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                            <BaseSwitch v-model="form.enabled_footer_hours" :label="$t('website_settings.footer.hours_label')" />
                        </div>
                    </div>

                    <!-- DashedCard for Inputs -->
                    <transition name="fade">
                        <DashedCard
                            v-if="form.enabled_footer_hours"
                            customClass="ppv2-p-8 ppv2-flex ppv2-flex-col ppv2-gap-6 lg:ppv2-w-[60%]"
                            :marginTop="37"
                            :marginBottom="30"
                        >
                            <template #title>
                                <BaseSwitch
                                    v-model="form.enabled_footer_hours"
                                    :label="$t('website_settings.footer.hours_label')"
                                    customLabelClass="poppinsLight ppv2-text-[#021832]"
                                    variant="purple"
                                />
                            </template>
                            <template #content>
                                <div v-for="h in form.hours" :key="h.day" class="ppv2-flex ppv2-items-center ppv2-gap-8">
                                    <div
                                        class="ppv2-w-28 poppinsSemiBold ppv2-text-black capitalize"
                                        :style="{ fontSize: normalizeFontSize(14) }"
                                    >
                                        {{ $t('website_settings.footer.days.' + h.day) }}
                                    </div>
                                    <div class="ppv2-flex-1">
                                        <CustomInput
                                            v-model="h.value"
                                            :placeholder="$t('website_settings.footer.hours_placeholder')"
                                            customInputClass="!ppv2-h-12 !ppv2-rounded-2xl"
                                            :customSizeLabel="14"
                                        />
                                    </div>
                                </div>
                            </template>
                        </DashedCard>
                    </transition>
                </div>
            </SectionCard>

            <!-- Réseaux sociaux -->
            <SectionCard :title="$t('website_settings.footer.social_networks')" :hideToggle="true">
                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-x-8 ppv2-gap-y-6">
                    <!-- Facebook -->
                    <CustomInput
                        v-model="form.facebook"
                        :label="$t('website_settings.footer.social.facebook')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.facebook')"
                    >
                        <template #label-icon>
                            <i class="fab fa-facebook ppv2-mr-1 ppv2-text-blue-600"></i>
                        </template>
                    </CustomInput>

                    <!-- LinkedIn -->
                    <CustomInput
                        v-model="form.linkedin"
                        :label="$t('website_settings.footer.social.linkedin')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.linkedin')"
                    >
                        <template #label-icon>
                            <i class="fab fa-linkedin ppv2-mr-1 ppv2-text-blue-700"></i>
                        </template>
                    </CustomInput>

                    <!-- Youtube -->
                    <CustomInput
                        v-model="form.youtube"
                        :label="$t('website_settings.footer.social.youtube')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.youtube')"
                    >
                        <template #label-icon>
                            <i class="fab fa-youtube ppv2-mr-1 ppv2-text-red-600"></i>
                        </template>
                    </CustomInput>

                    <!-- Twitter/X -->
                    <CustomInput
                        v-model="form.twitter"
                        :label="$t('website_settings.footer.social.twitter')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.twitter')"
                    >
                        <template #label-icon>
                            <i class="fab fa-twitter ppv2-mr-1 ppv2-text-blue-400"></i>
                        </template>
                    </CustomInput>

                    <!-- Instagram -->
                    <CustomInput
                        v-model="form.instagram"
                        :label="$t('website_settings.footer.social.instagram')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.instagram')"
                    >
                        <template #label-icon>
                            <i class="fab fa-instagram ppv2-mr-1 ppv2-text-pink-600"></i>
                        </template>
                    </CustomInput>

                    <!-- TikTok -->
                    <CustomInput
                        v-model="form.tiktok"
                        :label="$t('website_settings.footer.social.tiktok')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.tiktok')"
                    >
                        <template #label-icon>
                            <i class="fab fa-tiktok ppv2-mr-1 ppv2-text-black"></i>
                        </template>
                    </CustomInput>

                    <!-- Pinterest -->
                    <CustomInput
                        v-model="form.pinterest"
                        :label="$t('website_settings.footer.social.pinterest')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.pinterest')"
                    >
                        <template #label-icon>
                            <i class="fab fa-pinterest ppv2-mr-1 ppv2-text-red-700"></i>
                        </template>
                    </CustomInput>

                    <!-- Snapchat -->
                    <CustomInput
                        v-model="form.snapchat"
                        :label="$t('website_settings.footer.social.snapchat')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.snapchat')"
                    >
                        <template #label-icon>
                            <i class="fab fa-snapchat-ghost ppv2-mr-1 ppv2-text-yellow-400"></i>
                        </template>
                    </CustomInput>

                    <!-- Telegram -->
                    <CustomInput
                        v-model="form.telegram"
                        :label="$t('website_settings.footer.social.telegram')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.telegram')"
                    >
                        <template #label-icon>
                            <i class="fab fa-telegram ppv2-mr-1 ppv2-text-blue-500"></i>
                        </template>
                    </CustomInput>

                    <!-- Threads -->
                    <CustomInput
                        v-model="form.thread"
                        :label="$t('website_settings.footer.social.threads')"
                        customLabelClass="poppinsLight"
                        type="url"
                        :placeholder="$t('website_settings.footer.placeholders.threads')"
                    >
                        <template #label-icon>
                            <i class="fab fa-threads ppv2-mr-1 ppv2-text-black"></i>
                        </template>
                    </CustomInput>
                </div>
            </SectionCard>

            <!-- Analytics & Tracking
            <SectionCard :title="$t('website_settings.footer.analytics_tracking')" :hideToggle="true">
                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-gap-6 ppv2-mb-4">
                    <CustomInput
                        v-model="form.google_analytics"
                        :label="$t('website_settings.footer.analytics.google_analytics')"
                        customLabelClass="poppinsLight"
                        placeholder="GTM-XXXXXXX"
                    />
                    <CustomInput
                        v-model="form.facebook_pixel"
                        :label="$t('website_settings.footer.analytics.facebook_pixel')"
                        customLabelClass="poppinsLight"
                        placeholder="Pixel ID"
                    />
                </div>
                <BaseTextarea
                    v-model="form.Script"
                    :label="$t('website_settings.footer.custom_script')"
                    customLabelClass="poppinsLight"
                    :use-editor="false"
                    :rows="4"
                    :placeholder="$t('website_settings.footer.placeholders.custom_script')"
                    class="ppv2-font-mono ppv2-text-xs"
                />
            </SectionCard-->
        </form>
    </div>
</template>

<script>
import CustomInput from '../../../../Inputs/CustomInput.vue'
import CustomInputUpload from '../../../../Inputs/CustomInputUpload.vue'
import BaseTextarea from '../../../../Shared/BaseTextarea.vue'
import BaseButton from '../../../../Shared/BaseButton.vue'
import BaseSwitch from '../../../../Shared/BaseSwitch.vue'
import SectionCard from './SectionCard.vue'
import DashedCard from '../../../../Shared/DashedCard.vue'
// TODO: Uncomment when InfoBanner.vue is available
import InfoBanner from '../../../../Shared/InfoBanner.vue'
import normalizeSize from '../../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../../utils/normalizeFontSize'
export default {
    name: 'TabFooter',
    components: {
        CustomInput,
        CustomInputUpload,
        BaseTextarea,
        BaseButton,
        BaseSwitch,
        SectionCard,
        DashedCard,
        // TODO: Uncomment when InfoBanner.vue is available
        InfoBanner,
    },
    props: {
        data: { type: Object, default: () => ({}) },
    },
    computed: {
        establishmentId() {
            return this.data?.establishment_id || 1
        },
    },
    data() {
        return {
            saving: false,
            form: {
                // Global Toggle
                enabled_menu_footer: true,

                // Identity Section
                footer_logo_url: null,
                footer_logo_file: null,
                footer_logo_delete: false,

                // Presentation Section
                presentation_title: '',
                footer_desc: '',

                // Contact Section
                contact_us_title: '',
                contact_us_desc: '',
                txt_contact_us_title: '',

                // Opening Hours
                enabled_footer_hours: false,
                hours: [
                    { day: 'monday', value: '' },
                    { day: 'tuesday', value: '' },
                    { day: 'wednesday', value: '' },
                    { day: 'thursday', value: '' },
                    { day: 'friday', value: '' },
                    { day: 'saturday', value: '' },
                    { day: 'sunday', value: '' },
                ],

                // Social Networks
                linkedin: '',
                instagram: '',
                facebook: '',
                tiktok: '',
                twitter: '',
                youtube: '',
                pinterest: '',
                snapchat: '',
                telegram: '',
                thread: '',

                // Analytics
                google_analytics: '',
                facebook_pixel: '',
                Script: '',
            },
        }
    },
    mounted() {
        this.initializeForm()
    },
    activated() {
        this.$root.$off('website-settings-trigger-save-footer', this.save)
        this.$root.$on('website-settings-trigger-save-footer', this.save)
    },
    deactivated() {
        this.$root.$off('website-settings-trigger-save-footer', this.save)
    },
    beforeDestroy() {
        this.$root.$off('website-settings-trigger-save-footer', this.save)
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        normalizeSizeVertical,
        initializeForm() {
            const settings = this.data?.settings || []
            settings.forEach((s) => {
                const v = s.value
                if (this.form.hasOwnProperty(s.parameter)) {
                    if (s.parameter.startsWith('enabled_')) {
                        this.form[s.parameter] = v === 'on'
                    } else {
                        this.form[s.parameter] = v || ''
                    }
                }

                // Custom handling for new/special fields
                if (s.parameter === 'footer_logo') this.form.footer_logo_url = v
                if (s.parameter === 'enabled_footer_hours') this.form.enabled_footer_hours = v === 'on'

                // Hours mapping: footer_hours_monday, footer_hours_tuesday, etc.
                const hourMatch = s.parameter.match(/^footer_hours_(.+)$/)
                if (hourMatch) {
                    const day = hourMatch[1]
                    const hourObj = this.form.hours.find((h) => h.day === day)
                    if (hourObj) hourObj.value = v || ''
                }
            })
        },
        handleImageUpload(field, e) {
            const file = e.target.files[0]
            if (file) {
                const reader = new FileReader()
                reader.onload = (ev) => {
                    this.form[field + '_url'] = ev.target.result
                }
                reader.readAsDataURL(file)
                this.form[field + '_file'] = file
                this.form[field + '_delete'] = false
            } else {
                this.form[field + '_url'] = null
                this.form[field + '_file'] = null
                this.form[field + '_delete'] = true
            }
        },
        async save() {
            this.saving = true
            this.$emit('save', { loading: true })
            try {
                const fd = new FormData()

                // Add all simple fields
                Object.keys(this.form).forEach((key) => {
                    if (typeof this.form[key] !== 'object' || this.form[key] === null) {
                        if (key.startsWith('enabled_')) {
                            fd.append(key, this.form[key] ? 'on' : 'off')
                        } else {
                            fd.append(key, this.form[key] || '')
                        }
                    }
                })

                // Add hours
                this.form.hours.forEach((h) => {
                    fd.append(`footer_hours_${h.day}`, h.value || '')
                })

                // Add files
                if (this.form.footer_logo_file) {
                    fd.append('footer_logo', this.form.footer_logo_file)
                }
                if (this.form.footer_logo_delete) fd.append('footer_logo_delete', 1)

                const response = await axios.post('/back-office/website/website-settings', fd)
                this.$emit('save', { loading: false, success: true, message: response.data?.message })
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.$emit('save', { loading: false })
            } finally {
                this.saving = false
            }
        },
    },
}
</script>

<style scoped>
.section-block {
    border-radius: 20px;
    padding: 24px;
    box-shadow:
        0 4px 6px -1px rgba(0, 0, 0, 0.1),
        0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.dark .section-block {
    border-color: rgba(255, 255, 255, 0.05);
}
</style>
